'use client';

import { Box, Typography, Paper, Link, Snackbar, Alert, Stack } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import { useSystemConfig } from '@/hooks/useSystemConfig';
import { formatErrorMessage } from '@/lib/utils/apiErrors';

/**
 * Props for the ErrorPage component.
 */
interface ErrorPageProps {
  /**
   * The error to display.
   */
  error?: string | Error | unknown;

  /**
   * System configuration data.
   */
  systemConfig?: any;

  /**
   * Determines if the error should be displayed as a full page.
   * @default false
   */
  fullPage?: boolean;
}

/**
 * Displays an error message either as a snackbar or a full page.
 * Uses the backend's ProblemDetails detail when available, falling back to the
 * JS error message or a generic string.
 */
export default function ErrorPage({ error, systemConfig: propSystemConfig, fullPage = false }: ErrorPageProps) {
  const storeConfig = useSystemConfig();
  const effectiveSystemConfig = propSystemConfig ?? storeConfig;
  const contactEmail = effectiveSystemConfig?.dataManagerEmail ?? '2dcc-datamanagement@psu.edu';

  const errorMessage = error
    ? formatErrorMessage(error, 'An unexpected error occurred.')
    : 'An unexpected error occurred.';

  // For non-full-page errors, return a Snackbar
  if (!fullPage) {
    return (
      <Snackbar open={true} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert
          severity="error"
          sx={{ width: '100%' }}
          action={
            <Link href={`mailto:${contactEmail}`} color="inherit" underline="hover">
              Contact Support
            </Link>
          }
        >
          <Typography variant="subtitle2" component="div" gutterBottom>
            System Error
          </Typography>
          {errorMessage}
        </Alert>
      </Snackbar>
    );
  }

  // Full-page error content
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        p: 3,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 600,
          width: '100%',
          textAlign: 'center',
        }}
      >
        <ErrorIcon color="error" sx={{ fontSize: 64, mb: 2 }} />

        <Typography variant="h5" component="h1" gutterBottom>
          System Error
        </Typography>

        <Stack spacing={2}>
          <Typography variant="body1" color="text.secondary">
            {errorMessage}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            For assistance, please contact:{' '}
            <Link href={`mailto:${contactEmail}`} underline="hover" color="primary">
              {contactEmail}
            </Link>
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
}
